/*
 * Copyright 2025 Intuition Robotics ltd
 */
import * as React from "react";
import {useContext, useEffect, useRef} from "react";
import {BaseComponent, Dialog_Builder} from "@intuitionrobotics/thunderstorm/frontend";
import {InboxTable} from "./InboxTable";
import {COLORS} from "@res/colors";
import {ChatBox} from "./ChatBox";
import {FONTS} from "@res/fonts";
import {SearchBar} from "../SearchBar";
import {HBar} from "../../widget/HBar";
import {OKButton} from "../../widget/OKButton";
import {ComposeMessageDialog} from "./ComposeMessageDialog";
import {ScheduledMessagesPanel} from "./ScheduledMessagesPanel";
import {CalendarDialog, TimeAndPretty} from "./CalendarDialog";
import {OnWindowResize} from "../../../App";
import {SelectOrganization} from "../../widget/SelectOrganization";
import {tableCardStyle} from "../../home/AlertsTab";
import {margin_l_lg, margin_r_lg} from "@styles/styles";
import {PARAM_CHAT_ID, PARAM_CONTACT_ID} from "../../../Routes";
import {ProgressBar} from "../../widget/ProgressBar";
import {SelectMessageStatus} from "../../widget/SelectMessageStatus";
import {PatientContextProvider, PatientsContext, PatientsContextType} from "../patients/PatientsContext";
import {ID_ALL, messagingRoles} from "@app/app-shared/permission";
import {DB_Patient} from "@app/app-shared/patient";
import {ChatsContext, ChatsContextType} from "./ChatsContext";
import {getRelevantGroupContactIds} from "@modules/utils";
import {MessagingModule} from "@modules/MessagingModule";
import {filterDuplicates} from "@intuitionrobotics/ts-common";
import {ScheduledMessageInfo} from "@app/ir-q-app-common/types/scheduled-messages";
import {MessageUpdateType, PushKey_OnMessageUpdate} from "@app/app-shared/message";
import {SubscriptionDataRegistration} from "@intuitionrobotics/push-pub-sub";
import {PushPubSubModule} from "@intuitionrobotics/push-pub-sub/app-frontend/modules/PushPubSubModule";
import {Body_NotifyUpdatedStatuses, IncomingCCMessage} from "@app/ir-q-app-common/types/message-api";
import {DB_MessageStatus, MessageStatuses} from "@app/ir-q-app-common/types/message";
import {OrganizationsContext} from "../organizations/OrganizationsContext";
import {useHistory, useLocation} from "react-router-dom";
import {ContactsContext} from "./ContactsContext";
import {Loader} from "../../../../widgets/Loader";

export type InboxTab = "inbox" | "scheduled"

type MessagesTabState = {
    selectedTab: InboxTab
    text: string;
    search: string,
    openCalender: boolean,
    orgFilter: Set<string>
    filterDate?: {
        start: TimeAndPretty
        end: TimeAndPretty
    }
    filterByMessage: MessageFilter
}

type MessagesTapProps = { chatId?: string, selectedContactId?: string, onSelectedChat: (selectedContactId?: string, chatId?: string) => void }

export enum MessageFilter {
    ALL = "All Messages",
    UNREAD = "Unread Messages",
    NO_REPLY = "No Reply"
}

export const messagingTableRowHeight = 47;
export const messageTableHeadHeight = 34;

export const Page_Messages = () => {
    const {organizations} = useContext(OrganizationsContext);
    const {contacts} = useContext(ContactsContext);
    const {demo} = useContext(PatientsContext)
    const {saveChats, saveMessages, updateMessages, chats, messages, alreadyInitialized, setAlreadyInitialized} = useContext(ChatsContext);
    const location = useLocation();
    const history = useHistory();

    const subscriptionCallback = async (notification: any) => {
        switch (notification.type) {
            case MessageUpdateType.NEW:
                const incomingMessage = notification.data as IncomingCCMessage;
                const message = incomingMessage.message;
                const [groupContactId] = incomingMessage.groupContactIds;
                const foundChat = chats?.[message.chatId];
                if (foundChat)
                    foundChat.unreadCount = foundChat.unreadCount + 1;
                else {
                    const _chats = await MessagingModule.queryChat(contacts, groupContactId, message.chatId);
                    saveChats(_chats);
                    return;
                }
                const updateStatus: DB_MessageStatus = {...foundChat.lastMessage.status, ...{status: MessageStatuses.RECEIVED}};
                foundChat.lastMessage = {...message, ...{status: updateStatus}};
                saveChats([foundChat])

                const relevantMessages = messages?.[foundChat._id];
                if (relevantMessages) {
                    saveMessages([{...message, status: updateStatus}]);
                }
                return;
            case MessageUpdateType.UPDATE:
                const data = notification.data as Body_NotifyUpdatedStatuses;
                for (const groupContactId of Object.keys(data)) {
                    await Promise.all(data[groupContactId].map(async _chatId => {
                        const {messages: _messages} = await MessagingModule.queryMessages(_chatId, groupContactId, 10);
                        updateMessages(_messages);

                        const _foundChat = chats?.[_chatId];
                        if (!_foundChat) {
                            const _chats = await MessagingModule.queryChat(contacts, groupContactId, _chatId);
                            saveChats(_chats);
                            return;
                        }
                        _foundChat.lastMessage = _messages[_messages.length - 1];
                        saveChats([_foundChat])
                    }));
                }
        }
    };

    const subscriptionCallbackRef = useRef(subscriptionCallback);

    // Update the ref whenever the callback changes
    useEffect(() => {
        subscriptionCallbackRef.current = subscriptionCallback;
    }, [subscriptionCallback]);

    useEffect(() => {
        if (alreadyInitialized || demo)
            return;

        const groupIds = getRelevantGroupContactIds(organizations);
        MessagingModule.queryChats(contacts, filterDuplicates(groupIds), saveChats).then(() => setAlreadyInitialized()).catch(console.error);
        PushPubSubModule.subscribeMulti(groupIds.reduce((carry: SubscriptionDataRegistration[], _id) => {
            carry.push({
                pushKey: PushKey_OnMessageUpdate,
                props: {groupContactId: _id},
                callBack: (data) => subscriptionCallbackRef.current(data)
            });
            return carry;
        }, []));

    }, []);

    useEffect(() => {
        if (Object.keys(contacts).length && chats && !demo)
            saveChats(MessagingModule.filterNonConnectedChats(Object.values(chats), contacts));
    }, [contacts]);

    const queryParams = new URLSearchParams(location.search);
    const chatId = queryParams.get(PARAM_CHAT_ID) || undefined;
    const selectedRecipient = queryParams.get(PARAM_CONTACT_ID) || undefined;

    if (!Object.keys(contacts).length)
        return <Loader/>;

    return <Page_MessagesClass chatId={chatId} selectedContactId={selectedRecipient}
                               onSelectedChat={(selectedContactId, _chatId) => {
                                   const _queryParams = new URLSearchParams(location.search);

                                   if (!_chatId)
                                       _queryParams.delete(PARAM_CHAT_ID)
                                   else
                                       _queryParams.set(PARAM_CHAT_ID, _chatId)

                                   if (!selectedContactId)
                                       _queryParams.delete(PARAM_CONTACT_ID)
                                   else
                                       _queryParams.set(PARAM_CONTACT_ID, selectedContactId)

                                   history.push({
                                       pathname: location.pathname,
                                       search: _queryParams.toString()
                                   });
                               }}/>;
}

class Page_MessagesClass
    extends BaseComponent<MessagesTapProps, MessagesTabState>
    implements OnWindowResize {

    constructor(props: MessagesTapProps) {
        super(props);

        this.state = {
            search: "",
            text: "",
            selectedTab: "inbox",
            openCalender: false,
            orgFilter: new Set<string>(),
            filterByMessage: MessageFilter.ALL
        };
    }

    __onWindowResize = () => {
        this.forceUpdate();
    };

    clearTimestampFilter = () => {
        this.setState({filterDate: undefined});
    };

    renderDateFilter = () => {
        const date_placeholder = "mm/dd/yy";
        return <div className={"ll_v_s"}>
            <div className={"date-label"}>Date</div>
            <div className={"ll_h_c"} style={{display: "contents"}}>
                <div style={{
                    border: `1px solid ${COLORS.neutral_gray_2}`,
                    borderRadius: 2,
                    width: 190
                }}>
                    <div style={{position: "relative"}}>
                        <div className={"clickable"} onClick={() => this.setState(state => ({openCalender: !state.openCalender}))}>
                            {FONTS.reg_14(`${this.state.filterDate?.start.name || date_placeholder} - ${this.state.filterDate?.end.name || date_placeholder}`,
                                {
                                    color: this.state.filterDate?.start.name ? COLORS.neutral_text_dark : COLORS.neutral_gray_2,
                                    padding: "5px 10px",
                                    whiteSpace: "nowrap"
                                })}
                        </div>
                        {
                            this.state.openCalender &&
                            <>
                                <div className={"full_screen"} onClick={() => this.setState({openCalender: false})}/>
                                <div style={{position: "fixed", transform: "translate(0%)", marginTop: 10, zIndex: 15}}>
                                    <CalendarDialog
                                        onFilter={(startDate, endDate) => {
                                            this.setState({filterDate: {start: startDate, end: endDate}, openCalender: false});
                                        }}
                                        onClear={this.clearTimestampFilter}
                                        filterDate={this.state.filterDate}
                                    />
                                </div>
                            </>
                        }
                    </div>
                </div>
            </div>
        </div>;
    };

    private renderTabButton = (tab: InboxTab, label: string) => {
        const isActive = this.state.selectedTab === tab;
        return <div
            className={"clickable"}
            onClick={() => this.setState({selectedTab: tab})}
            style={{
                padding: "6px 16px",
                borderBottom: isActive ? `2px solid ${COLORS.semantic_blue}` : "2px solid transparent",
                fontWeight: isActive ? "bold" : "normal",
                color: isActive ? COLORS.semantic_blue : COLORS.grays_4,
                whiteSpace: "nowrap"
            }}>
            {FONTS.reg_14(label, {color: isActive ? COLORS.semantic_blue : COLORS.grays_4, fontWeight: isActive ? "bold" : "normal"})}
        </div>;
    };

    private renderHead = (cCtx: ChatsContextType, pCtx: PatientsContextType) => (
        <div className={`ll_h_r`} style={{justifyContent: "space-between", minHeight: 40, alignItems: "center"}}>
            <div className={`${margin_r_lg} ${margin_l_lg} ll_v_s`}
                 style={{flex: 0, marginTop: 8, minHeight: 70, justifyContent: "space-between", display: "-webkit-inline-box"}}>
                <div className={"ll_h_c"} style={{marginRight: 16}}>
                    {this.renderTabButton("inbox", "Inbox")}
                    {this.renderTabButton("scheduled", "Scheduled")}
                </div>
                {this.state.selectedTab !== "scheduled" && <>
                    <div className={"ll_h_c"} style={{width: 210}}>
                        <SearchBar
                            color={"rgb(244, 247, 247)"}
                            value={this.state.search}
                            onValueChange={value => {
                                this.setState({search: value});
                            }}
                            placeholder={"Search"}
                            onClose={() => {
                                this.setState({search: ""});
                            }}
                        />
                        <HBar/>
                    </div>
                    <div className={`ll_h_c ${margin_l_lg}`}>
                        {this.renderDateFilter()}
                    </div>
                    <div className={`ll_h_c ${margin_l_lg}`}>
                        <SelectOrganization
                            onOrgClicked={(orgId) => {
                                if (orgId === ID_ALL)
                                    return this.setState(s => ({...s, orgFilter: new Set()}));
                                this.setState(s => {
                                    const orgs = s.orgFilter;
                                    if (orgs.has(orgId))
                                        orgs.delete(orgId);
                                    else
                                        orgs.add(orgId);
                                    return {...s, orgFilter: orgs};
                                });
                            }}
                            selected={this.state.orgFilter}
                        />
                    </div>
                    <div className={`ll_h_c ${margin_l_lg}`}>
                        {<SelectMessageStatus
                            selectedMessageFilter={this.state.filterByMessage}
                            updateMessageFilter={(messageFilter: MessageFilter) => {
                                this.setState({filterByMessage: messageFilter});
                            }}
                        />}
                    </div>
                </>}
            </div>

            <div className={`ll_h_c ${margin_r_lg}`}>
                {!cCtx.alreadyInitialized && this.state.selectedTab !== "scheduled" && <ProgressBar
                    progressKey={"chats"}
                    style={{width: 300, marginRight: 8}} hideOnComplete={true}
                />}
                {this.renderComposeMessageButton(pCtx)}
            </div>
        </div>);

    private renderComposeMessageButton({patientIds}: PatientsContextType) {
        const patientsNumber = patientIds?.length;
        if (patientsNumber === 0)
            return null;

        return <OKButton text={"Compose message"} textStyle={{whiteSpace: "nowrap"}} enabled={true} onClick={() => {
            new Dialog_Builder(<ComposeMessageDialog/>)
                .addStyle({backgroundColor: "unset", boxShadow: "unset"})
                .setZIndex(100)
                .show();
        }}/>;
    }

    private deduceChatId = ({chats}: ChatsContextType, pCtx: PatientsContextType) => {
        const patient = this.getRecipient(pCtx);
        if (!patient)
            return;

        return Object.values(chats || {}).find(chat => chat.contactIds.includes(patient.contactId))?._id;
    }

    private handleMessagesSent = async (sentMessages: ScheduledMessageInfo[], cCtx: ChatsContextType) => {
        if (this.state.selectedTab !== "inbox")
            return;

        const chatsMap = cCtx.chats || {};

        for (const msg of sentMessages) {
            const recipientContactIds = msg.sendBody.contactIds || [];
            const groupContactId = msg.sendBody.senderId;
            if (!groupContactId)
                continue;

            const relevantChats = Object.values(chatsMap).filter(chat =>
                chat.contactIds.some(cId => recipientContactIds.includes(cId))
            );

            for (const chat of relevantChats) {
                try {
                    const {messages} = await MessagingModule.queryMessages(chat._id, groupContactId, 10);
                    if (!messages.length)
                        continue;
                    cCtx.updateMessages(messages);
                    cCtx.saveChats([{...chat, lastMessage: messages[messages.length - 1]}]);
                } catch (e) {
                    console.error("Failed to refresh chat after scheduled message sent", e);
                }
            }
        }
    };

    private renderBody(cCtx: ChatsContextType, pCtx: PatientsContextType) {
        if (this.state.selectedTab === "scheduled") {
            return <div
                id={"all-patients-table-wrapper"}
                className={"match_height flex-col space-between"}
                style={{...tableCardStyle}}
            >
                <ScheduledMessagesPanel onMessagesSent={(sent) => this.handleMessagesSent(sent, cCtx)}/>
            </div>;
        }

        const recipient = this.getRecipient(pCtx);
        const chatId = this.props.chatId || this.deduceChatId(cCtx, pCtx);

        return <div
            id={"all-patients-table-wrapper"}
            className={"match_height flex-col space-between"}
            style={{...tableCardStyle}}
        >
            <InboxTable
                demo={pCtx.demo}
                search={this.state.search}
                filterDate={this.state.filterDate}
                filterByMessage={this.state.filterByMessage}
                orgFilter={this.state.orgFilter}
                selectedPatientContactId={this.props.selectedContactId}
                chatId={chatId}
                onSelected={(selectedRecipient, _chatId) => this.props.onSelectedChat(selectedRecipient, _chatId)}
            />
            {this.renderChatBox(cCtx, recipient, chatId)}
        </div>;
    }

    private renderChatBox({chats}: ChatsContextType, recipient?: DB_Patient, chatId?: string) {
        return recipient && chatId && chats?.[chatId] && <ChatBox
            recipient={recipient}
            chatId={chatId}
            onClose={() => {
                this.props.onSelectedChat(undefined, undefined);
            }}
        />;
    }

    private getRecipient = ({patientIds, patients}: PatientsContextType): DB_Patient | undefined => {
        if (!this.props.selectedContactId)
            return;
        for (const p of patientIds) {
            const patient = patients[p];
            if (patient?.contactId === this.props.selectedContactId)
                return patient;
        }
    }

    render() {
        return <PatientContextProvider roles={messagingRoles}>{
            (pCtx) => {
                return <ChatsContext.Consumer>
                    {cCtx => {
                        return <div className={"fill flex-col"}>
                            {this.renderHead(cCtx, pCtx)}
                            {this.renderBody(cCtx, pCtx)}
                        </div>
                    }}
                </ChatsContext.Consumer>
            }}
        </PatientContextProvider>
    }
}
